/*Copyright (c) 2015, salesforce.com, inc. All rights reserved.
Copyright (c) 2019, Picsa Advisory Services (Pty) Ltd. All rights reserved.

Redistribution and use in source and binary forms, with or without modification, are permitted provided that the following conditions are met:
Redistributions of source code must retain the above copyright notice, this list of conditions and the following disclaimer.
Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the following disclaimer in the documentation and/or other materials provided with the distribution.
Neither the name of salesforce.com, inc. nor the names of its contributors may be used to endorse or promote products derived from this software without specific prior written permission.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES LOSS OF USE, DATA, OR PROFITS OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
*/

import React from 'react'
import PropTypes from 'prop-types'
import { graphql } from 'react-apollo'
import gql from 'graphql-tag'
import * as Sentry from '@sentry/browser'
import moment from 'moment'
import DatePicker from 'react-datepicker'

import {
  InfoDisplay,
  Input,
  SpanWithData,
  SelectWithData,
  IdentityPicsaScore
} from '../'

import {
  isValidSouthAfricanID,
  hasResponsibility
} from '../../utils'


const initialiseState = (identity) => {
  let { firstName, otherName, lastName, identityType, identityValue, dateOfBirth, language } = identity

  return {
    edit: false,
    firstName,
    otherName,
    lastName,
    identityType,
    identityValue,
    dateOfBirth,
    language,
    saveInProgress: false
  }
}


class IdentityDetails extends React.PureComponent {
  static propTypes = {
    identity: PropTypes.object.isRequired
  }

  state = initialiseState(this.props.identity)

  identityFieldChanged = () => (
    this.props.identity.firstName !== this.state.firstName ||
    this.props.identity.otherName !== this.state.otherName ||
    this.props.identity.lastName !== this.state.lastName ||
    this.props.identity.dateOfBirth !== this.state.dateOfBirth ||
    (this.state.identityType && this.props.identity.identityType !== this.state.identityType.toUpperCase()) ||
    this.props.identity.identityValue !== this.state.identityValue ||
    (this.state.language && this.props.identity.language !== this.state.language.toUpperCase())
  )

  handleSave = () => {
    this.setState({ saveInProgress: true })

    let identity = this.props.identity
    let input = {
      id: identity.id
    }

    if (identity.firstName !== this.state.firstName) {
      input.firstName = this.state.firstName
    }
    if (identity.otherName !== this.state.otherName) {
      input.otherName = this.state.otherName
    }
    if (identity.lastName !== this.state.lastName) {
      input.lastName = this.state.lastName
    }
    if (identity.identityType !== this.state.identityType) {
      input.identityType = this.state.identityType
    }
    if (identity.identityValue !== this.state.identityValue) {
      input.identityValue = this.state.identityValue
    }
    if (identity.dateOfBirth !== this.state.dateOfBirth) {
      input.dateOfBirth = this.state.dateOfBirth.format('YYYY-MM-DD')
    }
    if (identity.language !== this.state.language) {
      input.language = this.state.language
    }

    this.props.saveIdentity({ variables: { input } })
      .then(() => {
        this.setState({ edit: false, saveInProgress: false })
      })
      .catch(error => {
        this.setState({ edit: false, saveInProgress: false })
        Sentry.captureMessage('Error saving identity details.')
      })
  }

  toggleEdit = () => this.setState(prevState => ({ edit: !prevState.edit }))

  handleDetailChange = (e) => this.setState({ [e.target.id]: e.target.value })
  handleBirthDateChange = (date) => this.setState({ dateOfBirth: date })

  isValidIdentityValue = (identityType, identityValue) => (
    identityType && identityType.toLowerCase() === 'za_id'
      ? isValidSouthAfricanID(identityValue)
      : identityValue.length > 0  // too many variations to properly validate passport numbers
  )

  render() {
    let { firstName, otherName, lastName, identityType, identityValue, dateOfBirth, language } = this.props.identity

    return (
      <InfoDisplay
        title='Identity details'
        rows={
          <tbody>
            <tr>
              <th className='slds-size_1-of-4 slds-theme_shade slds-text-heading_label'>
                Name
              </th>
              <td>
                {this.state.edit
                  ? <div className='slds-grid'>
                      <Input
                        id='firstName'
                        placeholder='First name'
                        value={this.state.firstName || ''}
                        onChange={this.handleDetailChange}
                      />
                      <Input
                        id='otherName'
                        placeholder='Other name'
                        value={this.state.otherName || ''}
                        onChange={this.handleDetailChange}
                        className='slds-m-left_x-small'
                      />
                      <Input
                        id='lastName'
                        placeholder='Last name'
                        value={this.state.lastName || ''}
                        onChange={this.handleDetailChange}
                        className='slds-m-left_x-small'
                      />
                    </div>
                  : `${firstName}${otherName ? ` (${otherName})` : ''} ${lastName}`
                }
              </td>
            </tr>
            <tr>
              <th className='slds-size_1-of-4 slds-theme_shade slds-text-heading_label'>
                Identitification
              </th>
              <td>
                {this.state.edit && hasResponsibility('self', 'identity_value_update', 'identity', null)
                  ? <div className='slds-grid'>
                      <Input
                        id='identityValue'
                        placeholder='Identity number'
                        value={this.state.identityValue || ''}
                        onChange={this.handleDetailChange}
                        hasError={!this.isValidIdentityValue(this.state.identityType, this.state.identityValue)}
                      />
                      <SelectWithData
                        id='identityType'
                        forField='IdentityIdentityType'
                        value={this.state.identityType}
                        onChange={this.handleDetailChange}
                        hasError={this.state.identityValue && !this.state.identityType}
                        noDefaultOption={true}
                        className='slds-m-left_x-small'
                      />
                    </div>
                  : <div className='slds-grid'>
                      {identityValue}
                      <div className='slds-m-left_x-small'>
                        (
                        {identityType
                          ? <SpanWithData forField='IdentityIdentityType' searchKey={identityType} />
                          : 'not specified'
                        }
                        )
                      </div>
                    </div>
                }
              </td>
            </tr>
            <tr>
              <th className='slds-size_1-of-4 slds-theme_shade slds-text-heading_label'>
                Date of birth
              </th>
              <td>
                {this.state.edit && identityType === 'PASSPORT'
                  ? <DatePicker
                      selected={this.state.dateOfBirth
                        ? moment(this.state.dateOfBirth)
                        : null
                      }
                      onChange={this.handleBirthDateChange}
                      showMonthDropdown={true}
                      showYearDropdown={true}
                      maxDate={moment()}
                      dateFormat="D MMM YYYY"
                      className='slds-input'
                    />
                  : dateOfBirth
                    ? moment(this.state.dateOfBirth, 'YYYY-MM-DD').format('D MMMM YYYY')
                    : '-'
                }
              </td>
            </tr>
            <tr>
              <th className='slds-size_1-of-4 slds-theme_shade slds-text-heading_label'>
                Preferred language
              </th>
              <td>
                {this.state.edit
                  ? <SelectWithData
                      id='language'
                      placeholder='Language'
                      forField='IdentityLanguage'
                      value={this.state.language}
                      onChange={this.handleDetailChange}
                      noDefaultOption={true}
                    />
                  : <SpanWithData
                      forField='IdentityLanguage'
                      searchKey={language}
                    />
                }
              </td>
            </tr>
            <tr>
              <th className='slds-size_1-of-4 slds-theme_shade slds-text-heading_label'>
                Picsa score
              </th>
              <td>
                <IdentityPicsaScore identityId={this.props.identity.id} />
              </td>
            </tr>
          </tbody>
        }
        edit={this.state.edit}
        onCancelClick={this.toggleEdit}
        onSaveClick={this.handleSave}
        onEditClick={this.toggleEdit}
        saveDisabled={!this.identityFieldChanged() || this.state.saveInProgress}
        saveButtonLabel={this.state.saveInProgress ? 'Saving' : null}
        noFixedLayout={true}
        titleBold={false}
        className='slds-m-bottom_x-large'
      />
    )
  }
}

const saveIdentity = gql`
  mutation ($input: IdentityMutationInput!) {
    identityMutate(input: $input) {
      identity {
        id
        dateOfBirth
        firstName
        otherName
        lastName
        identityType
        identityValue
        language
      }
    }
  }
`

const withIdentityMutate =
  graphql(saveIdentity, {
    name: 'saveIdentity'
  })

export default withIdentityMutate(IdentityDetails)



// WEBPACK FOOTER //
// ./src/components/identity-contact-details/IdentityDetails.js